<!-- /live once an event is over, before its end state (LiveEndState.vue; Seán, 2026-11-03):
     a Net Promoter Score, 0 to 10 across the card, any other thoughts, and Share Feedback, each
     the card's full width; after Mastery's (Feedback/NetPromoterScore.vue: its question, its
     ends, a segmented bar). Sharing records it (useEventFeedback.js) and reveals the end state.
     In the test mode's Final State nothing is recorded (isPreview). -->

<template>
  <form class="feedback" @submit.prevent="share">
    <fieldset>
      <!-- the event by its full name; both at once, VIZBI Online (FEEDBACK.md) -->
      <legend v-if="eventKeys.length > 1">
        How likely are you to recommend VIZBI <i>Online</i> to others?
      </legend>
      <legend v-else>
        How likely are you to recommend the VIZBI <i>Online</i> {{ getEventType(eventKeys[0]) }}
        to others?
      </legend>
      <div class="scores">
        <label v-for="value in SCORES" :key="value" class="score">
          <input v-model="score" type="radio" name="score" :value="value" />
          <span>{{ value }}</span>
        </label>
      </div>
      <div class="ends" aria-hidden="true">
        <span>Very unlikely</span>
        <span>Very likely</span>
      </div>
    </fieldset>
    <textarea
      v-model="comment"
      rows="3"
      aria-label="What most influenced your rating? Any other thoughts?"
      placeholder="What most influenced your rating? Any other thoughts?" />
    <p v-if="errorMessage" class="error" role="alert">{{ errorMessage }}</p>
    <!-- given before: Save Changes (and Cancel, the answer kept as it was) -->
    <button type="submit" class="share" :disabled="score === null || isSaving">
      {{ buttonLabel }}
    </button>
    <button v-if="isUpdate" type="button" class="cancel" @click="emit('cancel')">Cancel</button>
  </form>
</template>

<script setup>
import {computed, ref} from "vue";
import useUserSafe from "@/composables/useUserSafe";
import {fetchSaveFeedback, getEventType} from "@/composables/useEventFeedback";

const props = defineProps({
  eventKeys: {type: Array, required: true}, // ["masterclass-2026"], or both events at once
  isPreview: {type: Boolean, default: false}, // Final State: shown, not recorded
  initial: {type: Object, default: null}, // the answer given before: {netPromoterScore, comments}
  isUpdate: {type: Boolean, default: false} // changing it (Update Feedback: Seán, 2026-11-03)
});
const emit = defineEmits(["shared", "cancel"]);
const SCORES = Array.from({length: 11}, (_, value) => value);
const {user} = useUserSafe();
const [score, comment] = [ref(props.initial?.netPromoterScore ?? null),
  ref(props.initial?.comments || "")];
const [isSaving, errorMessage] = [ref(false), ref("")];
const buttonLabel = computed(() => {
  if (isSaving.value) return props.isUpdate ? "Saving…" : "Sharing…";
  return props.isUpdate ? "Save Changes" : "Share Feedback";
});

async function share() {
  if (isSaving.value) return; // one request at a time
  const answer = {netPromoterScore: score.value, comments: comment.value.trim()};
  if (props.isPreview) return emit("shared", answer);
  [isSaving.value, errorMessage.value] = [true, ""];
  try {
    await fetchSaveFeedback(user.value, props.eventKeys, answer);
    emit("shared", answer);
  } catch (error) {
    console.error(`LiveFeedback: ${error}`);
    errorMessage.value = "Your feedback couldn't be saved. Please try again."; // entries kept
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped>
.feedback {
  display: flex;
  flex-direction: column;
}
/* the hierarchy (Seán, 2026-11-03): the page's title, then the question (smaller, brighter, not
   bold), then the muted introduction (LiveEndState.vue) */
fieldset {
  margin: 0 0 32px; /* ▶ EDIT HERE: space between the scores' ends and the text box */
  padding: 0;
  border: none;
}
legend {
  margin-bottom: 24px; /* ▶ EDIT HERE: space between the question and the scores */
  padding: 0;
  font-size: 1.15rem;
  font-weight: 500;
  color: var(--text-color-strong);
}
/* 0 to 10 across the card's full width, each the same */
.scores {
  display: grid;
  grid-template-columns: repeat(11, 1fr);
  gap: 1px; /* one segmented bar, as Mastery's */
  border-radius: 8px;
  overflow: hidden;
}
.score input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.score span {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  background: color-mix(in srgb, var(--text-color) 12%, transparent);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.score input:not(:checked) + span:hover {
  background: color-mix(in srgb, var(--brand-color) 30%, transparent);
}
.score input:checked + span {
  background: var(--brand-dark-color);
  color: white;
}
.score input:focus-visible + span {
  outline: 2px solid var(--brand-color);
  outline-offset: -2px;
}
.ends {
  display: flex;
  justify-content: space-between;
  margin-top: 8px; /* the scores and their ends, one group */
  font-size: 0.9rem;
  color: color-mix(in srgb, var(--text-color) 65%, transparent);
}
textarea {
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 32px; /* ▶ EDIT HERE: space between the text box and Share Feedback */
  padding: 12px;
  border: 1.5px solid color-mix(in srgb, var(--text-color) 35%, transparent);
  border-radius: 8px;
  background: transparent;
  color: var(--text-color);
  font: inherit;
  resize: vertical;
}
.error {
  margin: 0 0 12px;
  color: var(--red, #d33);
}
/* an action button (DESIGN.md § 5): white on the darker brand green, the card's full width */
button.share {
  width: 100%;
  min-height: 44px;
  margin: 0;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  background: var(--brand-dark-color);
  color: white;
  font: inherit;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
}
button.share:hover:not(:disabled) {
  filter: brightness(1.15);
}
/* no score yet: neutral grey, not a faded green, and no hover */
/* Cancel, under Save Changes: quiet, a text button (the answer kept as it was) */
button.cancel {
  display: block;
  margin: 12px auto 0;
  padding: 4px 8px;
  border: none;
  box-shadow: none;
  background: none;
  color: color-mix(in srgb, var(--text-color) 70%, transparent);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}
button.share:disabled {
  background: color-mix(in srgb, var(--text-color) 16%, transparent);
  color: color-mix(in srgb, var(--text-color) 60%, transparent);
  cursor: not-allowed;
}
button.share:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
</style>
